import { useToolEvents } from "./ToolEvents";
import { userMessage } from "../../utils/userMessage";
import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ToolToolbar } from "./ToolToolbar";
import { WorkList } from "./WorkList";
import { useToolListState } from "../../features/toolExperience/useToolListState";
import { useToolScope } from "../../features/toolExperience/useToolScope";
import { pageItems } from "../../features/toolExperience/listState";
import type { ToolId } from "../../features/toolExperience/types";
import type { WorkListItem } from "./types";
import styles from "./ToolPage.module.css";
export function ProjectCollection({
  toolId,
  items,
  loading,
  error,
  onRetry,
  emptyContent,
  recent,
  recentLabel = "Recent results",
  statusOptions = [],
}: {
  toolId: ToolId;
  items: WorkListItem[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyContent?: ReactNode;
  recent?: ReactNode;
  recentLabel?: string;
  statusOptions?: Array<{ value: string; label: string }>;
}) {
  const scope = useToolScope();
  const { search: locationSearch } = useLocation();
  const focusSearch =
    new URLSearchParams(locationSearch).get("focus") === "search";
  useEffect(() => {
    if (focusSearch)
      requestAnimationFrame(() =>
        document
          .querySelector<HTMLInputElement>('main input[type="search"]')
          ?.focus(),
      );
  }, [focusSearch]);
  const { state, setState, clearFilters, rememberPosition } = useToolListState(
    scope,
    toolId,
    ["all", ...statusOptions.map((s) => s.value)],
  );
  const track = useToolEvents();
  const lastFilter = useRef(`${state.q}:${state.status}`);
  useEffect(() => {
    const current = `${state.q}:${state.status}`;
    if (current === lastFilter.current) return;
    const timer = window.setTimeout(() => {
      lastFilter.current = current;
      track?.("filter_applied");
    }, 400);
    return () => window.clearTimeout(timer);
  }, [state.q, state.status, track]);
  const filtered = items.filter(
    (item) =>
      (!state.q ||
        `${item.title} ${item.description || ""}`
          .toLocaleLowerCase()
          .includes(state.q.toLocaleLowerCase())) &&
      (state.status === "all" ||
        item.status?.label.toLowerCase() === state.status),
  );
  filtered.sort((a, b) => {
    const byName = a.title.localeCompare(b.title);
    if (state.sort === "name") return byName;
    const dateA = Date.parse(a.updatedAt || "") || 0,
      dateB = Date.parse(b.updatedAt || "") || 0;
    return dateB - dateA || byName;
  });
  const paged = pageItems(filtered, state.page);
  return (
    <>
      {recent && (
        <div className={styles.tabs} aria-label="Collection view">
          <button
            type="button"
            aria-pressed={state.view === "projects"}
            onClick={() => setState({ view: "projects", q: "", status: "all" })}
          >
            Projects
          </button>
          <button
            type="button"
            aria-pressed={state.view === "recent"}
            onClick={() => setState({ view: "recent", q: "", status: "all" })}
          >
            {recentLabel}
          </button>
        </div>
      )}
      {recent && state.view === "recent" ? (
        recent
      ) : (
        <>
          <ToolToolbar
            search={{
              label: "Search projects",
              value: state.q,
              onChange: (q) => setState({ q }),
            }}
            filters={
              statusOptions.length > 0 && (
                <select
                  aria-label="Project status"
                  value={state.status}
                  onChange={(e) => setState({ status: e.target.value })}
                >
                  <option value="all">All statuses</option>
                  {statusOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )
            }
            sort={
              <select
                aria-label="Sort projects"
                value={state.sort}
                onChange={(e) =>
                  setState({ sort: e.target.value as "updated" | "name" })
                }
              >
                <option value="updated">Recent first</option>
                <option value="name">Name</option>
              </select>
            }
            resultLabel={
              loading
                ? "Loading projects…"
                : error && !items.length
                  ? "Projects unavailable"
                  : `${filtered.length} ${error ? "previously loaded " : ""}${filtered.length === 1 ? "project" : "projects"}`
            }
          />
          {(state.q || state.status !== "all") && (
            <button
              type="button"
              className={styles.secondary}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
          <WorkList
            items={paged.items}
            state={loading ? "loading" : error ? "error" : "ready"}
            errorContent={
              <>
                <p>
                  {userMessage(
                    error,
                    "Could not load these projects. Please try again.",
                  )}
                </p>
                {onRetry && (
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={onRetry}
                  >
                    Retry loading projects
                  </button>
                )}
              </>
            }
            emptyContent={
              state.q || state.status !== "all" ? (
                <p>No matching projects. Adjust your search or filters.</p>
              ) : (
                emptyContent || (
                  <p>
                    No projects yet. Start a task to create your first project.
                  </p>
                )
              )
            }
            pagination={{ ...paged, onChange: (page) => setState({ page }) }}
            onOpen={rememberPosition}
          />
        </>
      )}
    </>
  );
}
